import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Star,
  Check,
  TrendingUp,
  Analytics,
  Visibility,
  Download,
  Security
} from '@mui/icons-material';
import { useRole } from '../../contexts/RoleContext';

const SubscriptionModal = ({ open, onClose, feature }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { upgradeToPro } = useRole();

  const proFeatures = [
    {
      icon: <Visibility />,
      title: 'Full Property Details',
      description: 'View all property columns including upset value and profit calculations'
    },
    {
      icon: <TrendingUp />,
      title: 'Market Analysis',
      description: 'Access detailed property analysis and investment metrics'
    },
    {
      icon: <Analytics />,
      title: 'Advanced Analytics',
      description: 'Get comprehensive insights and market trends'
    },
    {
      icon: <Download />,
      title: 'Data Export',
      description: 'Export property data for further analysis'
    },
    {
      icon: <Security />,
      title: 'Priority Support',
      description: 'Get priority customer support and assistance'
    }
  ];

  const handleUpgrade = async () => {
    setLoading(true);
    setError('');
    
    try {
      await upgradeToPro();
      onClose();
      // Show success message or refresh page
      window.location.reload();
    } catch (err) {
      console.error('Upgrade error:', err);
      setError('Failed to upgrade subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Star sx={{ color: 'gold', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" component="h2" fontWeight="bold">
              Upgrade to Pro
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unlock premium features for better investment decisions
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card 
          elevation={3}
          sx={{ 
            mb: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Pro Subscription
            </Typography>
            <Typography variant="h2" fontWeight="bold" sx={{ mb: 1 }}>
              FREE
            </Typography>
            <Chip 
              label="Limited Time Offer" 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 'bold'
              }} 
            />
            <Typography variant="body1" sx={{ mt: 2, opacity: 0.9 }}>
              Get instant access to all premium features
            </Typography>
          </CardContent>
        </Card>

        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Check color="success" />
          What's included in Pro:
        </Typography>

        <List>
          {proFeatures.map((feature, index) => (
            <React.Fragment key={index}>
              <ListItem sx={{ py: 2 }}>
                <ListItemIcon sx={{ color: 'primary.main' }}>
                  {feature.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight="medium">
                      {feature.title}
                    </Typography>
                  }
                  secondary={feature.description}
                />
              </ListItem>
              {index < proFeatures.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>

        {feature && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="body2" color="text.primary">
              <strong>You tried to access:</strong> {feature}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This feature is available with Pro subscription. Upgrade now to access this and many more premium features!
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          disabled={loading}
        >
          Maybe Later
        </Button>
        <Button 
          onClick={handleUpgrade}
          variant="contained"
          size="large"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <Star />}
          sx={{ 
            minWidth: 150,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
            }
          }}
        >
          {loading ? 'Upgrading...' : 'Upgrade Now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionModal; 